import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Buffer } from 'buffer';
import './boggle.css';

const Boggle = () => {
    const [input, setInput] = useState('');
    const [grid, setGrid] = useState([]);
    const [results, setResults] = useState([]);
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const isSquareNumber = (num) => {
        return Number.isInteger(Math.sqrt(num));
    };

    const initializeFromURL = () => {
        const letters = searchParams.get('letters');
        const encoding = searchParams.get('encoding') || 'plaintext';
        if (letters) {
            let decodedLetters;
            try {
                if (encoding === 'base64') {
                    decodedLetters = Buffer.from(letters, 'base64').toString('utf-8');
                } else if (encoding === 'plaintext') {
                    decodedLetters = letters;
                } else {
                    throw new Error('Invalid encoding parameter.');
                }
            } catch {
                setError('Invalid letters or encoding parameter in URL.');
                return;
            }
            if (/^[a-zA-Z]+$/.test(decodedLetters) && isSquareNumber(decodedLetters.length)) {
                setInput(decodedLetters.toUpperCase());
                handleSubmit(null, decodedLetters.toUpperCase());
            } else {
                setError('Invalid letters parameter in URL.');
            }
        }
    };

    useEffect(() => {
        initializeFromURL();
    }, []);

    const handleSubmit = async (e, customInput = null) => {
        if (e) e.preventDefault();
        const inp = (customInput || input).toUpperCase();
        if (!/^[a-zA-Z]+$/.test(inp)) {
            setError('Input must contain only alphabet characters.');
            return;
        }
        if (!isSquareNumber(inp.length)) {
            setError('Input length must be a square number.');
            return;
        }

        setError('');
        const size = Math.sqrt(inp.length);
        const newGrid = [];
        for (let i = 0; i < size; i++) {
            newGrid.push(inp.slice(i * size, (i + 1) * size).split(''));
        }
        setGrid(newGrid);

        try {
            const response = await fetch(`boggle/${inp.toLowerCase()}`, {
                method: 'GET'
            });
            const data = await response.json();
            if (data.error) {
                setError(data.error);
            } else {
                setResults(data.value);
                setSubmitted(true);
                const encodedLetters = Buffer.from(inp.toLowerCase()).toString('base64');
                setSearchParams({ letters: encodedLetters, encoding: 'base64' });
            }
        } catch (err) {
            setError('Failed to fetch results from the API.');
        }
    };

    const handleReset = () => {
        setInput('');
        setGrid([]);
        setResults([]);
        setError('');
        setSubmitted(false);
        setSearchParams({});
    };

    return (
        <div className="boggle-container">
            <h3>Boggle Solver</h3>
            <p className="boggle-subtitle">Find all valid words from your letter grid</p>
            <form onSubmit={handleSubmit} className="boggle-form">
                {!submitted && (
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Enter board letters"
                    />
                )}
                {!submitted && <button type="submit">Solve</button>}
                {submitted && <button type="button" onClick={handleReset}>Reset</button>}
            </form>
            {error && <p className="error">{error}</p>}
            {grid.length > 0 && (
                <div className="grid-wrapper">
                    <div className="grid" style={{ gridTemplateColumns: `repeat(${Math.sqrt(input.length)}, 52px)` }}>
                        {grid.flat().map((char, index) => (
                            <div key={index} className="grid-item">
                                {char}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {results.length > 0 && (
                <div className="results-header">
                    <span>Found</span>
                    <span className="results-count">{results.length} words</span>
                </div>
            )}
            <ul className="results">
                {results.map((result, index) => (
                    <li key={index}>{result}</li>
                ))}
            </ul>
        </div>
    );
};

export default Boggle;
